import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X } from "lucide-react";
import { PageHeader, GlassCard, EmptyState, Field, GradButton } from "@/components/erp";
import { SelectBox } from "@/components/Inputs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { errMsg } from "@/lib/api";

const DURUM = { bekliyor: "Bekliyor", onaylandi: "Onaylandı", reddedildi: "Reddedildi" };

const OnayDialog = ({ b, cariler, onClose, onDone }) => {
  const [cari, setCari] = useState("yeni");
  const onayla = async () => {
    try {
      const { data } = await api.post(`/bayi-basvurulari/${b.id}/onayla`, { cari_id: cari === "yeni" ? null : cari });
      toast.success(`${data.cari_unvan} için bayi girişi açıldı; şifre belirleme bağlantısı ${b.email} adresine gönderildi`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="glass" data-testid="basvuru-onay-dialog">
        <DialogHeader><DialogTitle>{b.unvan}</DialogTitle></DialogHeader>
        <Field label="Hangi cariye bağlansın?">
          <SelectBox testid="basvuru-cari" value={cari} onChange={setCari} options={[{ value: "yeni", label: "Başvuru bilgileriyle yeni cari aç" },
            ...cariler.filter((c) => c.tip !== "tedarikci").map((c) => ({ value: c.id, label: `${c.kod} · ${c.unvan}` }))]} />
        </Field>
        <p className="text-xs text-muted-foreground">Bayi kullanıcısı açılır ve şifresini belirlemesi için 48 saat geçerli bir bağlantı e-postayla gönderilir. Risk limitini ve kâr oranını cari kartından ayarlayın.</p>
        <div className="flex justify-end gap-2"><button onClick={onClose} className="px-5 py-2 rounded-full border border-white/10 text-sm">Vazgeç</button>
          <GradButton onClick={onayla} data-testid="basvuru-onay-kaydet">Onayla</GradButton></div>
      </DialogContent>
    </Dialog>
  );
};

export default function BayiBasvurulari() {
  const [list, setList] = useState([]);
  const [cariler, setCariler] = useState([]);
  const [sec, setSec] = useState(null);
  const load = useCallback(async () => {
    const [b, c] = await Promise.all([api.get("/bayi-basvurulari"), api.get("/cariler")]);
    setList(b.data); setCariler(c.data);
  }, []);
  useEffect(() => { load(); }, [load]);
  const reddet = async (id) => { try { await api.post(`/bayi-basvurulari/${id}/reddet`); toast.success("Başvuru reddedildi"); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div data-testid="bayi-basvurulari-page">
      <PageHeader eyebrow="Ticari" title="Bayi Başvuruları" desc={`Siteden gelen başvurular. Başvuru formu: ${window.location.origin}/bayi-basvuru`} />
      <GlassCard className="overflow-x-auto">
        {list.length === 0 ? <EmptyState text="Henüz başvuru yok." testid="basvuru-bos" /> : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b border-white/5">
              <th className="p-4">Tarih</th><th className="p-4">Firma</th><th className="p-4">İletişim</th><th className="p-4">Durum</th><th className="p-4" /></tr></thead>
            <tbody>{list.map((b) => (
              <tr key={b.id} className="border-b border-white/5" data-testid={`basvuru-satir-${b.email}`}>
                <td className="p-4 text-xs text-muted-foreground whitespace-nowrap">{new Date(b.created_at).toLocaleString("tr-TR")}</td>
                <td className="p-4"><div className="font-medium">{b.unvan}</div><div className="text-xs text-muted-foreground">{[b.vergi_dairesi, b.vergi_no, b.il, b.ilce].filter(Boolean).join(" · ")}</div>
                  {b.mesaj && <div className="text-xs text-muted-foreground mt-1 italic">"{b.mesaj}"</div>}</td>
                <td className="p-4 text-xs"><div>{b.yetkili}</div><div className="text-muted-foreground">{b.email} · {b.telefon}</div></td>
                <td className="p-4 text-xs" data-testid={`basvuru-durum-${b.email}`}>{DURUM[b.durum]}{b.islem_yapan ? ` · ${b.islem_yapan}` : ""}</td>
                <td className="p-4 text-right whitespace-nowrap">{b.durum === "bekliyor" && <>
                  <button data-testid={`basvuru-onayla-${b.email}`} onClick={() => setSec(b)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs text-emerald-300 hover:bg-white/10"><Check className="w-4 h-4" />Onayla</button>
                  <button data-testid={`basvuru-reddet-${b.email}`} onClick={() => window.confirm(`${b.unvan} başvurusu reddedilsin mi?`) && reddet(b.id)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs text-red-300 hover:bg-white/10"><X className="w-4 h-4" />Reddet</button></>}</td>
              </tr>))}</tbody>
          </table>
        )}
      </GlassCard>
      {sec && <OnayDialog b={sec} cariler={cariler} onClose={() => setSec(null)} onDone={() => { setSec(null); load(); }} />}
    </div>
  );
}
